import { useEffect, useRef, useState } from 'react';

import { useStrings } from '@/i18n/Localization';
import type { StringKey } from '@/i18n/strings';
import type { DisplayStatus } from '@/worker/connectionController';
import styles from '@/app/ConnectionStatusRow.module.css';

/**
 * CR-01 §4: the connection status row.
 *
 * It belongs to *this* account's connection to our service. It is not another
 * person's online state, not a typing indicator and not a message receipt —
 * conflating those is how a status row ends up lying about one of them.
 *
 * Three properties the amendment insists on, each for a concrete reason:
 *
 *   - **Fixed height, always rendered, including Connected.** Space is reserved
 *     so a transition never moves the message list or the composer under
 *     somebody's cursor. The previous banner appeared and vanished, which is
 *     exactly the layout shift this replaces.
 *   - **Supplied state, never inferred.** A shared selector feeds both the inbox
 *     and the conversation screen. A component that inferred status from its own
 *     last request would disagree with the one next to it.
 *   - **Never colour alone.** Every state carries an icon and text, so the
 *     information survives a reader who cannot distinguish the tint.
 */

interface Labels {
  primary: StringKey;
  detail: StringKey | null;
}

const LABELS: Readonly<Record<DisplayStatus, Labels>> = {
  SIGN_IN: { primary: 'connection.signIn', detail: 'connection.signIn.detail' },
  UPDATE_CLIENT: { primary: 'connection.updateClient', detail: 'connection.updateClient.detail' },
  CERTIFICATE_FAILURE: {
    primary: 'connection.certificateFailure',
    detail: 'connection.certificateFailure.detail',
  },
  WAITING_FOR_NETWORK: {
    primary: 'connection.waitingForNetwork',
    detail: 'connection.waitingForNetwork.detail',
  },
  CONNECTING: { primary: 'connection.connecting', detail: 'connection.connecting.detail' },
  UPDATING: { primary: 'connection.updating', detail: 'connection.updating.detail' },
  UPDATING_DELAYED: { primary: 'connection.updating', detail: 'connection.updating.delayed' },
  CONNECTED: { primary: 'connection.connected', detail: null },
};

/** One glyph per state. Text always accompanies it; this is not the only signal. */
const ICONS: Readonly<Record<DisplayStatus, string>> = {
  SIGN_IN: '!',
  UPDATE_CLIENT: '!',
  CERTIFICATE_FAILURE: '!',
  WAITING_FOR_NETWORK: '⚠',
  CONNECTING: '◌',
  UPDATING: '↻',
  UPDATING_DELAYED: '↻',
  CONNECTED: '✓',
};

const ACTIONABLE: ReadonlySet<DisplayStatus> = new Set<DisplayStatus>([
  'SIGN_IN',
  'UPDATE_CLIENT',
  'CERTIFICATE_FAILURE',
]);

export interface ConnectionStatusRowProps {
  status: DisplayStatus;
  /** Messages committed locally and not yet acknowledged by the server. */
  pendingCount?: number;
  /** §8: shown while authenticated and otherwise ready, as a detail only. */
  slow?: boolean;
  /** §4: channel enumeration is reported inside the inbox, not as the status. */
  refreshingChannels?: boolean;
  /** Seconds until the next automatic attempt, when one is scheduled. */
  retryInSeconds?: number | null;
  onRetryNow?: () => void;
  onAction?: (status: DisplayStatus) => void;
}

export function ConnectionStatusRow(props: ConnectionStatusRowProps) {
  const { t } = useStrings();
  const { status } = props;
  const labels = LABELS[status];
  const actionable = ACTIONABLE.has(status);

  const [expanded, setExpanded] = useState(false);

  /**
   * §4: announce a changed primary label through a polite live region, after the
   * debounce has already settled it, and do not move focus.
   *
   * The announcement is deliberately only the primary label. Announcing a retry
   * countdown would speak over the reader once a second, which is why the
   * amendment forbids it explicitly.
   */
  const announced = useRef<DisplayStatus | null>(null);
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (announced.current === status) return;
    announced.current = status;
    setAnnouncement(t(labels.primary));
  }, [status, labels.primary, t]);

  const detail = (() => {
    if (status === 'CONNECTED') {
      return props.slow ? t('connection.connected.slow') : null;
    }
    if (status === 'CONNECTING' && typeof props.retryInSeconds === 'number') {
      return t('connection.connecting.retryIn', { seconds: String(props.retryInSeconds) });
    }
    return labels.detail === null ? null : t(labels.detail);
  })();

  const pending = props.pendingCount ?? 0;

  return (
    <div className={styles.row} data-status={status}>
      <div className={styles.primary}>
        <span className={styles.icon} aria-hidden="true">
          {ICONS[status]}
        </span>
        <span className={styles.label}>{t(labels.primary)}</span>

        {/*
          The detail panel is opt-in. §4 restricts what it may contain: status,
          the pending count and Retry now — never a token, an endpoint address,
          an administrator identity or a raw protocol error.
        */}
        {detail !== null || pending > 0 || props.onRetryNow ? (
          <button
            type="button"
            className={styles.disclosure}
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            {t('connection.status')}
          </button>
        ) : null}

        {actionable && props.onAction ? (
          <button
            type="button"
            className={styles.action}
            onClick={() => props.onAction?.(status)}
          >
            {t(labels.primary)}
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className={styles.detail}>
          {detail !== null ? <p className={styles.detailText}>{detail}</p> : null}
          {pending > 0 ? (
            <p className={styles.detailText}>
              {t('connection.pendingCount', { count: String(pending) })}
            </p>
          ) : null}
          {/*
            Retry now is offered, but the controller owns whether it does
            anything: §5 gives it one accelerated round per five seconds and
            forbids it from bypassing a server-directed delay. The button is not
            a second retry loop.
          */}
          {props.onRetryNow ? (
            <button type="button" className={styles.retry} onClick={props.onRetryNow}>
              {t('connection.retryNow')}
            </button>
          ) : null}
        </div>
      ) : null}

      {props.refreshingChannels ? (
        <p className={styles.secondary}>{t('connection.refreshingChannels')}</p>
      ) : null}

      <div className={styles.liveRegion} role="status" aria-live="polite">
        {announcement}
      </div>
    </div>
  );
}
